import {
  copyBusinessTimeFromBot,
  copyBusinessTimeFromBotFailure,
  copyBusinessTimeFromBotSuccess,
  fetchBusinessTime,
  fetchBusinessTimeFailure,
  fetchBusinessTimeSuccess,
  removeBusinessTime,
  removeBusinessTimeFailure,
  removeBusinessTimeSuccess,
  resetRequestState,
  updateBusinessTime,
  updateBusinessTimeFailure,
  updateBusinessTimeSuccess,
} from '@src/reducers/businessTime';

import B2ChatClient from '@client-sdk';
import {
  BusinessTime,
  BusinessTimeAgentPriority,
  BusinessTimeAssistant,
  BusinessTimePerHolidays,
} from '@src/components/AdminHome/BusinessTime/businessTime.type';
import { isErrorPayload } from '@src/services/service';
import { DepartmentType } from '@src/types';
import { fetchAssistantApi } from '@src/types/assistant';
import env from '@src/utils/env';
import { B2ChatAPI } from '@types-api';
import { format, parse } from 'date-fns';
import { call, delay, put, takeLatest, takeLeading } from 'redux-saga/effects';

export function parseHolidaysDateStringToDate(
  bizTimesPerHoliDay: BusinessTimePerHolidays
): BusinessTimePerHolidays {
  return bizTimesPerHoliDay?.map(holiday => {
    if (holiday?.date && typeof holiday.date === 'string') {
      return {
        ...holiday,
        date: parse(holiday.date, 'yyyy-MM-dd', new Date()),
      };
    }
    return holiday;
  });
}

export type FetchAssistantsSafeError = Error & { status?: number };
async function fetchAssistantsSafe(
  departmentType: string,
  departmentId?: string | number
): Promise<BusinessTimeAssistant[]> {
  try {
    const assistants = await fetchAssistantApi<BusinessTimeAssistant[]>(
      departmentType === DepartmentType.STANDARD
        ? `${env.WEB_SERVICE_ASSISTANTS_BASE_URI}/assistants/general`
        : `${env.WEB_SERVICE_ASSISTANTS_BASE_URI}/departments/${departmentId}/assistant`,
      { method: 'GET' }
    );
    return assistants ?? [];
  } catch (err: unknown) {
    if (err instanceof Error) {
      const m = err.message.match(/\b(\d{3})\b/); // extrae 404 del mensaje
      if (m && Number(m[1]) === 404) return [];
    }

    throw err;
  }
}

function* fetchBusinessTimeSaga({
  payload: { botId, departmentId, departmentType },
}: ReturnType<typeof fetchBusinessTime>) {
  try {
    const businessTimeResponse: B2ChatAPI.Response<{
      bizTimesPerWeekDay: BusinessTime['timesPerWeekDay'];
      bizTimesPerHoliDay: BusinessTime['timesPerHoliDay'];
    }> = yield call(
      B2ChatClient.resources.businessTime.actions.getBusinessTime,
      { params: { botId } }
    );

    if (businessTimeResponse.error) throw businessTimeResponse.error;

    const merchantResponse: B2ChatAPI.Response<{
      chatManagement: { receiveChatsNoAvailability: boolean };
      servicePriorityAgentEnum?: BusinessTimeAgentPriority;
    }> = yield call(
      B2ChatClient.resources.merchant.actions.merchantAvailability,
      {
        params: { botId },
      }
    );

    if (merchantResponse.error) throw merchantResponse.error;

    const assistants: BusinessTimeAssistant[] = yield call(
      fetchAssistantsSafe,
      departmentType,
      departmentId
    );

    const bizTimesPerHoliDay =
      businessTimeResponse?.data?.bizTimesPerHoliDay ?? [];

    yield put(
      fetchBusinessTimeSuccess({
        timesPerHoliDay: parseHolidaysDateStringToDate(bizTimesPerHoliDay),
        timesPerWeekDay: businessTimeResponse?.data?.bizTimesPerWeekDay ?? [],
        merchantAvailability: {
          agentPriority:
            merchantResponse?.data?.servicePriorityAgentEnum ||
            BusinessTimeAgentPriority.AI_AGENT,
          receivedChatsNoAvailability:
            merchantResponse?.data?.chatManagement?.receiveChatsNoAvailability,
        },
        assistants,
      })
    );
  } catch (error) {
    const formattedError = isErrorPayload(error)
      ? error
      : { errorCode: 'UNKNOWN', details: (error as Error).message };

    yield put(fetchBusinessTimeFailure(formattedError));
  } finally {
    yield delay(1000);
    yield put(resetRequestState());
  }
}

function* copyBusinessTimeFromBotSaga({
  payload: { botId },
}: ReturnType<typeof copyBusinessTimeFromBot>) {
  try {
    const businessTimeFromBotResponse: B2ChatAPI.Response<{
      bizTimesPerWeekDay: BusinessTime['timesPerWeekDay'];
      bizTimesPerHoliDay: BusinessTime['timesPerHoliDay'];
    }> = yield call(
      B2ChatClient.resources.businessTime.actions.getBusinessTime,
      { params: { botId } }
    );

    if (businessTimeFromBotResponse.error)
      throw businessTimeFromBotResponse.error;

    const { bizTimesPerWeekDay: timesPerWeekDay, bizTimesPerHoliDay } =
      businessTimeFromBotResponse?.data || {};

    const timesPerHoliDay = parseHolidaysDateStringToDate(bizTimesPerHoliDay);

    yield put(
      copyBusinessTimeFromBotSuccess({ timesPerHoliDay, timesPerWeekDay })
    );
  } catch (error) {
    const formattedError = isErrorPayload(error)
      ? error
      : { errorCode: 'UNKNOWN', details: (error as Error).message };

    yield put(copyBusinessTimeFromBotFailure(formattedError));
  } finally {
    yield delay(1000);
    yield put(resetRequestState());
  }
}

function* updateBusinessTimeSaga({
  payload: {
    botId,
    merchantAvailability,
    noAgentsAvailableMessage,
    timesPerWeekDay,
    timesPerHoliDay,
  },
}: ReturnType<typeof updateBusinessTime>) {
  try {
    if (timesPerHoliDay?.length || timesPerWeekDay?.length) {
      const bizTimesPerHoliDay = timesPerHoliDay.map(holiday => {
        holiday.date = format(holiday.date, 'yyyy-MM-dd') as unknown as Date;
        return holiday;
      });

      const response: B2ChatAPI.Response<BusinessTime> = yield call(
        B2ChatClient.resources.businessTime.actions.updateBusinessTime,
        {
          data: {
            botId,
            businessTimesJson: JSON.stringify({
              bizTimesPerWeekDay: timesPerWeekDay,
              bizTimesPerHoliDay,
            }),
          },
        }
      );

      if (response.error) throw response.error;
    }

    if (
      noAgentsAvailableMessage &&
      Object.keys(noAgentsAvailableMessage).length > 0
    ) {
      const agentsAvailableMessageResponse: B2ChatAPI.Response<boolean> =
        yield call(
          B2ChatClient.resources.standardMessages.actions.updateMessage,
          {
            params: { id: botId },
            data: {
              code: 'NO_AGENTS_AVAILABLE',
              ...noAgentsAvailableMessage,
            },
          }
        );

      if (agentsAvailableMessageResponse.error)
        throw agentsAvailableMessageResponse.error;
    }

    if (merchantAvailability && Object.keys(merchantAvailability).length > 0) {
      const merchantResponse: B2ChatAPI.Response<BusinessTime> = yield call(
        B2ChatClient.resources.merchant.actions.updateMerchantAvailability,
        {
          params: { botId },
          data: {
            servicePriorityAgent:
              merchantAvailability?.agentPriority ||
              BusinessTimeAgentPriority.AI_AGENT,
            chatManagement: {
              receiveChatsNoAvailability:
                merchantAvailability.receivedChatsNoAvailability || null,
            },
          },
        }
      );

      if (merchantResponse.error) throw merchantResponse.error;
    }

    yield put(
      updateBusinessTimeSuccess({
        merchantAvailability,
        noAgentsAvailableMessage,
        timesPerWeekDay,
        timesPerHoliDay: parseHolidaysDateStringToDate(timesPerHoliDay),
      })
    );
  } catch (error) {
    const formattedError = isErrorPayload(error)
      ? error
      : { errorCode: 'UNKNOWN', details: (error as Error).message };

    yield put(updateBusinessTimeFailure(formattedError));
  } finally {
    yield delay(1000);
    yield put(resetRequestState());
  }
}

function* removeBusinessTimeSaga({
  payload: { botId },
}: ReturnType<typeof removeBusinessTime>) {
  try {
    const { error }: B2ChatAPI.Response<boolean> = yield call(
      B2ChatClient.resources.businessTime.actions.removeBusinessTime,
      {
        data: { botId },
      }
    );

    if (error) throw error;
    yield put(removeBusinessTimeSuccess());
  } catch (error) {
    const formattedError = !isErrorPayload(error)
      ? { errorCode: 'UNKNOWN', details: (error as Error).message }
      : error;

    yield put(removeBusinessTimeFailure(formattedError));
  } finally {
    yield delay(1000);
    yield put(resetRequestState());
  }
}

function* BusinessTimeSaga() {
  yield takeLatest(fetchBusinessTime, fetchBusinessTimeSaga);
  yield takeLatest(copyBusinessTimeFromBot, copyBusinessTimeFromBotSaga);
  yield takeLeading(updateBusinessTime, updateBusinessTimeSaga);
  yield takeLeading(removeBusinessTime, removeBusinessTimeSaga);
}

export default BusinessTimeSaga;
